import axios, {AxiosError, AxiosInstance, AxiosRequestConfig} from 'axios'
import config from '../config/steps.config'

interface StepsAuthResponse {
  Message: string
  IsAuthenticated: boolean
  ClientId: string
  Role: string
  BeginnerMode: string
  Token: string
}

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean
}

class StepsAuthService {
  private static instance: StepsAuthService
  private token: string | null = null
  private tokenExpiry: number | null = null
  private readonly axiosInstance: AxiosInstance
  private isRefreshing = false
  private refreshSubscribers: Array<(token: string) => void> = []
  private readonly tokenStorageKey = 'steps_auth_token'
  private readonly tokenExpiryKey = 'steps_auth_token_expiry'

  private constructor() {
    this.axiosInstance = axios.create({
      baseURL: config.baseUrl,
      timeout: config.requestTimeout,
      headers: {
        'Content-Type': 'application/json',
      },
      validateStatus: status => status >= 200 && status < 300,
    })

    this.setupAxiosInterceptors()
    this.loadStoredToken()
  }

  private loadStoredToken(): void {
    try {
      const storedToken = localStorage.getItem(this.tokenStorageKey)
      const storedExpiry = localStorage.getItem(this.tokenExpiryKey)

      if (storedToken && storedExpiry) {
        const expiry = parseInt(storedExpiry, 10)
        if (!isNaN(expiry) && expiry > Date.now()) {
          this.token = storedToken
          this.tokenExpiry = expiry
        } else {
          this.clearStoredToken()
        }
      }
    } catch (error) {
      console.error('Error loading stored token:', error)
      this.clearStoredToken()
    }
  }

  private storeToken(token: string, expiry: number): void {
    try {
      localStorage.setItem(this.tokenStorageKey, token)
      localStorage.setItem(this.tokenExpiryKey, expiry.toString())
    } catch (error) {
      console.error('Error storing token:', error)
    }
  }

  private clearStoredToken(): void {
    try {
      localStorage.removeItem(this.tokenStorageKey)
      localStorage.removeItem(this.tokenExpiryKey)
    } catch (error) {
      console.error('Error clearing stored token:', error)
    }
  }

  private setupAxiosInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      config => {
        // Add timestamp to prevent caching
        config.params = {
          ...config.params,
          _t: Date.now(),
        }
        return config
      },
      error => Promise.reject(error),
    )

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      response => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as CustomAxiosRequestConfig
        if (!originalRequest) {
          return Promise.reject(error)
        }

        // Handle 401 Unauthorized errors
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            return new Promise(resolve => {
              this.refreshSubscribers.push((token: string) => {
                originalRequest.headers = {
                  ...originalRequest.headers,
                  Authorization: `Bearer ${token}`,
                }
                resolve(this.axiosInstance(originalRequest))
              })
            })
          }

          originalRequest._retry = true
          this.isRefreshing = true

          try {
            const newToken = await this.refreshToken()
            this.refreshSubscribers.forEach(callback => callback(newToken))
            this.refreshSubscribers = []
            return this.axiosInstance(originalRequest)
          } catch (refreshError) {
            this.refreshSubscribers = []
            this.clearToken()
            return Promise.reject(refreshError)
          } finally {
            this.isRefreshing = false
          }
        }

        // Handle other errors
        if (error.response?.status === 403) {
          this.clearToken()
        }

        return Promise.reject(error)
      },
    )
  }

  public static getInstance(): StepsAuthService {
    if (!StepsAuthService.instance) {
      StepsAuthService.instance = new StepsAuthService()
    }
    return StepsAuthService.instance
  }

  private isTokenValid(): boolean {
    if (!this.token || !this.tokenExpiry) {
      return false
    }
    return Date.now() < this.tokenExpiry - config.tokenExpiryBuffer
  }

  private async refreshToken(): Promise<string> {
    let retries = 0
    let lastError: Error | null = null

    while (retries < config.maxRetries) {
      try {
        const response = await this.axiosInstance.post<StepsAuthResponse>(
          '/rest/admin/token',
          {
            ClientId: config.clientId,
            Token: config.clientToken,
          },
        )

        if (!response.data.IsAuthenticated) {
          throw new Error(`Authentication failed: ${response.data.Message}`)
        }

        this.token = response.data.Token
        this.tokenExpiry = Date.now() + 24 * 60 * 60 * 1000 // 24 hours
        this.storeToken(this.token, this.tokenExpiry)
        return this.token
      } catch (error) {
        lastError =
          error instanceof Error
            ? error
            : new Error('Unknown error during token refresh')
        retries++

        if (retries === config.maxRetries) {
          break
        }

        // Exponential backoff with jitter
        const delay = Math.min(
          config.retryDelay * Math.pow(2, retries) + Math.random() * 1000,
          config.maxRetryDelay,
        )
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }

    throw (
      lastError || new Error('Failed to refresh token after multiple attempts')
    )
  }

  public async getToken(): Promise<string> {
    if (this.isTokenValid()) {
      return this.token!
    }

    return this.refreshToken()
  }

  public async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await this.getToken()
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    }
  }

  public clearToken(): void {
    this.token = null
    this.tokenExpiry = null
    this.clearStoredToken()
  }
}

export default StepsAuthService
